import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { templateContent } from '@/js/util';

// Custom columns management inside the configure modals (spec sheets and the order tracker).
// The delete confirmation is either a nested UI::Dialog (dialog targets) or a UI::TwinTurboModal
// in a confirmTemplate target, opened on top of the configure modal.
export default class extends Controller {
  static targets = ['list', 'template', 'dialog', 'dialogColumnName', 'confirmTemplate'];
  static values = { checkUrl: String };

  #pendingRow;
  #nextIndex;
  #confirmModal;

  connect() {
    this.#nextIndex = this.listTarget.querySelectorAll(
      '.custom-columns__list-column--dynamic'
    ).length;
  }

  add() {
    const index = this.#nextIndex;
    this.#nextIndex += 1;
    const html = this.templateTarget.innerHTML.replaceAll(
      'config_items_attributes][0]',
      `config_items_attributes][${index}]`
    );

    this.listTarget.insertAdjacentHTML('beforeend', html);
    this.listTarget.lastElementChild.querySelector('.field--custom-columns-config').focus();
  }

  async remove(event) {
    const button = event.currentTarget;
    const row = button.closest('.custom-columns__list-column--dynamic');

    // Fresh rows are not persisted yet, so they can simply be dropped.
    if (!button.dataset.columnId) {
      this.#removeErrorMessage(row);
      row.remove();
      return;
    }

    const request = new FetchRequest('get', this.checkUrlValue, {
      responseKind: 'json',
      query: { custom_column_id: button.dataset.columnId },
    });

    let response;
    try {
      response = await request.perform();
    } catch {
      response = null;
    }
    if (!response?.ok) {
      window.alert('Something went wrong. Please try again.');
      return;
    }

    const { has_data: hasData } = await response.json;
    if (hasData && this.hasConfirmTemplateTarget) {
      this.#openConfirmModal(row, button.dataset.columnName);
    } else if (hasData) {
      this.#pendingRow = row;
      this.dialogColumnNameTarget.textContent = button.dataset.columnName;
      this.dialogColumnNameTarget.title = button.dataset.columnName;
      this.dialogTarget.showModal();
    } else {
      this.#markForDeletion(row);
    }
  }

  confirmDelete() {
    this.#markForDeletion(this.#pendingRow);
    this.#pendingRow = null;
    this.dialogTarget.close();
  }

  validateNames(event) {
    this.element.querySelectorAll('.custom-columns .error-message').forEach((el) => el.remove());

    const blankFields = [
      ...this.listTarget.querySelectorAll('.custom-columns__list-column--dynamic'),
    ]
      .filter((row) => !row.classList.contains('hide'))
      .map((row) => row.querySelector('.field--custom-columns-config'))
      .filter((field) => field.value.trim().length === 0);

    if (blankFields.length === 0) {
      return;
    }

    event.preventDefault();
    blankFields.forEach((field) => {
      field
        .closest('.custom-columns__list-column--dynamic')
        .insertAdjacentHTML(
          'afterend',
          '<div class="error-message error-message--visible error-message--m-mb">Please enter valid name</div>'
        );
    });
    blankFields[0].focus();
  }

  // The modal lives in the body, outside this controller, so its Delete button is wired here.
  // Clicking delete again while it is open opens nothing.
  #openConfirmModal(row, columnName) {
    if (this.#confirmModal?.isConnected) {
      return;
    }

    const modal = templateContent(this.confirmTemplateTarget);
    const name = modal.querySelector('[data-custom-column-name]');

    name.textContent = columnName;
    name.title = columnName;
    modal
      .querySelector('[data-custom-column-confirm]')
      .addEventListener('click', () => this.#markForDeletion(row));

    this.#confirmModal = modal;
    document.body.append(modal);
  }

  #markForDeletion(row) {
    row.querySelector('.custom-columns__delete-checkbox').checked = true;
    this.#removeErrorMessage(row);
    row.classList.add('hide');
  }

  #removeErrorMessage(row) {
    if (row.nextElementSibling?.classList.contains('error-message')) {
      row.nextElementSibling.remove();
    }
  }
}
